import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import { Tree } from '../../types/tree';
import { TreePopupCard } from './TreePopupCard';
import './GalleryMap.css';

interface GalleryMapProps {
  trees: Tree[];
  selectedSpecies: string[];
  selectedConservation: string[];
  selectedSizes: string[];
}

// Custom marker icons based on conservation status - tree icon
const createTreeIcon = (tree: Tree, isHighlighted: boolean = false) => {
  const nativeStatus = Number(tree.Native);
  const endemicStatus = Number(tree.Endemism);
  const invasiveStatus = Number(tree.Invasive);

  let color = '#4a7c59'; // Default forest green

  if (invasiveStatus === 1) {
    color = '#e74c3c'; // Red for invasive
  } else if (endemicStatus === 1) {
    color = '#8e44ad'; // Purple for endemic
  } else if (nativeStatus === 1) {
    color = '#27ae60'; // Green for native
  } else {
    color = '#3498db'; // Blue for non-native
  }

  const size = isHighlighted ? 28 : 24;

  return L.divIcon({
    className: 'custom-tree-marker',
    html: `
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); ${isHighlighted ? 'transform: scale(1.2);' : ''}">
        <path d="M12 2C12 2 7 6 7 10C7 12.21 8.79 14 11 14V22H13V14C15.21 14 17 12.21 17 10C17 6 12 2 12 2Z" fill="${color}" stroke="white" stroke-width="1.5"/>
        <path d="M8 10C8 10 5 12 5 14C5 15.1 5.9 16 7 16H17C18.1 16 19 15.1 19 14C19 12 16 10 16 10" fill="${color}" stroke="white" stroke-width="1.5"/>
      </svg>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
};

// Map bounds updater component
const MapBoundsUpdater: React.FC<{ trees: Tree[] }> = ({ trees }) => {
  const map = useMap();

  React.useEffect(() => {
    if (trees.length > 0) {
      const validTrees = trees.filter(t => t.Latitude && t.Longitude);
      if (validTrees.length > 0) {
        const bounds = L.latLngBounds(
          validTrees.map(t => [Number(t.Latitude), Number(t.Longitude)])
        );
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [trees, map]);

  return null;
};

// Component to force-enable layer control radio buttons
const LayerControlEnabler: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    // Function to enable all radio buttons in layer control
    const enableRadioButtons = () => {
      const radioButtons = document.querySelectorAll('.leaflet-control-layers-selector');
      radioButtons.forEach((radio) => {
        if (radio instanceof HTMLInputElement) {
          radio.disabled = false;
          radio.style.pointerEvents = 'auto';
          radio.style.cursor = 'pointer';
        }
      });
    };

    // Enable immediately after mount
    setTimeout(enableRadioButtons, 100);

    // Set up interval to continuously force-enable (in case Leaflet re-disables)
    const interval = setInterval(enableRadioButtons, 500);

    // Also enable on any layer control interactions
    map.on('baselayerchange', enableRadioButtons);
    map.on('layeradd', enableRadioButtons);
    map.on('layerremove', enableRadioButtons);

    // Observe DOM changes to re-enable if Leaflet modifies the DOM
    const observer = new MutationObserver(enableRadioButtons);
    const layerControl = document.querySelector('.leaflet-control-layers');
    if (layerControl) {
      observer.observe(layerControl, {
        attributes: true,
        subtree: true,
        attributeFilter: ['disabled']
      });
    }

    return () => {
      clearInterval(interval);
      map.off('baselayerchange', enableRadioButtons);
      map.off('layeradd', enableRadioButtons);
      map.off('layerremove', enableRadioButtons);
      observer.disconnect();
    };
  }, [map]);

  return null;
};

export const GalleryMap: React.FC<GalleryMapProps> = ({
  trees,
  selectedSpecies,
  selectedConservation,
  selectedSizes,
}) => {
  const [hoveredTree, setHoveredTree] = useState<string | null>(null);

  // Filter trees based on selected criteria
  const filteredTrees = useMemo(() => {
    return trees.filter(tree => {
      // Species filter
      if (selectedSpecies.length > 0 && !selectedSpecies.includes(tree.Species || '')) {
        return false;
      }

      // Conservation filter
      if (selectedConservation.length > 0) {
        const isNative = Number(tree.Native) === 1;
        const isEndemic = Number(tree.Endemism) === 1;
        const isInvasive = Number(tree.Invasive) === 1;
        // Use IUCN codes: VU (Vulnerable), EN (Endangered), CR (Critically Endangered), NT (Near Threatened)
        const threatStatus = String(tree.Threat_status || '').trim().toUpperCase();
        const isThreatened = threatStatus === 'VU' || threatStatus === 'EN' ||
                            threatStatus === 'CR' || threatStatus === 'NT';

        const matchesFilter = selectedConservation.some(filter => {
          if (filter === 'native' && isNative) return true;
          if (filter === 'endemic' && isEndemic) return true;
          if (filter === 'threatened' && isThreatened) return true;
          if (filter === 'invasive' && isInvasive) return true;
          if (filter === 'non-native' && !isNative && !isInvasive) return true;
          return false;
        });

        if (!matchesFilter) return false;
      }

      // Size filter (GBH >= 30cm only)
      if (selectedSizes.length > 0) {
        const gbh = Number(tree.GBH) || 0;
        const matchesSize = selectedSizes.some(size => {
          if (size === 'medium' && gbh >= 30 && gbh < 100) return true;
          if (size === 'large' && gbh >= 100 && gbh < 200) return true;
          if (size === 'extra-large' && gbh >= 200) return true;
          return false;
        });

        if (!matchesSize) return false;
      }

      return tree.Latitude && tree.Longitude;
    });
  }, [trees, selectedSpecies, selectedConservation, selectedSizes]);

  // Use ref to maintain stable initial center (prevents MapContainer recreation)
  const initialCenter = useRef<[number, number]>([18.5322, 73.8797]);

  // Calculate current center for MapBoundsUpdater
  const center: [number, number] = useMemo(() => {
    if (filteredTrees.length === 0) {
      return [18.5322, 73.8797]; // Pune coordinates
    }
    const lats = filteredTrees.map(t => Number(t.Latitude)).filter(Boolean);
    const lngs = filteredTrees.map(t => Number(t.Longitude)).filter(Boolean);
    const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
    const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
    return [avgLat, avgLng];
  }, [filteredTrees]);

  return (
    <div className="gallery-map">
      <div className="gallery-map__info">
        <div className="gallery-map__count">
          <span className="gallery-map__count-number">{filteredTrees.length}</span>
          <span className="gallery-map__count-label">
            {filteredTrees.length === 1 ? 'tree' : 'trees'} displayed
          </span>
        </div>
      </div>

      <MapContainer
        center={initialCenter.current}
        zoom={16}
        className="gallery-map__container"
        scrollWheelZoom={true}
        zoomControl={false}
        minZoom={3}
        maxZoom={20}
        whenReady={() => {
          console.log('Map ready - layers should be initialized');
        }}
      >
        <LayersControl position="bottomleft">
          {/* OpenStreetMap - Default street map */}
          <LayersControl.BaseLayer key="osm-standard" checked name="Street Map">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>

          {/* ESRI World Imagery - Satellite */}
          <LayersControl.BaseLayer key="esri-satellite" name="Satellite">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>

          {/* ESRI World Topo Map */}
          <LayersControl.BaseLayer key="esri-topo" name="Topographic">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>

          {/* ESRI World Terrain Base */}
          <LayersControl.BaseLayer key="esri-terrain" name="Terrain">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: USGS, Esri, TANA, DeLorme, and NPS'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}"
              maxZoom={13}
            />
          </LayersControl.BaseLayer>

          {/* ESRI World Shaded Relief */}
          <LayersControl.BaseLayer key="esri-relief" name="Shaded Relief">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}"
              maxZoom={13}
            />
          </LayersControl.BaseLayer>

          {/* ESRI World Street Map */}
          <LayersControl.BaseLayer key="esri-street" name="ESRI Streets">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <MapBoundsUpdater trees={filteredTrees} />
        <LayerControlEnabler />

        {filteredTrees.map((tree, index) => {
          if (!tree.Latitude || !tree.Longitude) return null;

          const position: [number, number] = [Number(tree.Latitude), Number(tree.Longitude)];
          const treeId = `${tree.Species}-${index}`;
          const isHighlighted = hoveredTree === treeId;

          return (
            <Marker
              key={treeId}
              position={position}
              icon={createTreeIcon(tree, isHighlighted)}
              eventHandlers={{
                mouseover: () => setHoveredTree(treeId),
                mouseout: () => setHoveredTree(null),
              }}
            >
              <Popup maxWidth={350} className="gallery-map__popup">
                <TreePopupCard tree={tree} />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default GalleryMap;
